import { Component, Vue, Prop } from "vue-property-decorator";
import { CreateElement, VNode } from "vue";
import Card from "@/components/Card";
import GlobalRules from "@/views/auth/rules";
import firebase from "firebase/app";
import "firebase/firestore";
import { configurationCollection, usersCollection } from "@/firebase";
import VueRecaptcha from "vue-recaptcha";
import RecapchaContainer from "@/components/Recaptcha.vue";

interface FormModel {
  key: string;
  label: string;
  type: string;
  // For the radio button groups assign the 'props' key with an object that matches given type annotation
  props: { key: string; label: string; value: any; other: any }[];
  disabled?: boolean;
  hidden?: boolean;
}

@Component({
  components: { VueRecaptcha }
})
export default class ConsentPage extends Vue {
  // This variables builds the form
  @Prop() formModel!: FormModel[];
  // Format eg. [['title', 'description']]
  // 'titles' cannot have the same name
  @Prop() content!: string[][];
  @Prop() title!: string;
  // Form type - unique identifier of the specific form
  @Prop() type!: string;
  @Prop({ default: false }) showRecaptcha!: boolean;
  // Routing on submit
  @Prop({ default: "/" }) routing!: string;
  @Prop() collection!: firebase.firestore.CollectionReference;
  @Prop({ default: null }) consentFormData!: any;
  @Prop({ default: "Submit" }) buttonLabel!: string;
  @Prop({ default: null }) callbackfn!: () => void;
  @Prop({ default: null }) callbackOnConsentReject!: () => void;
  @Prop({ default: true }) showNotification!: boolean;

  private teacherNameEnable: boolean = false;
  private _schoolAndTeachers: any = {};
  private _schoolList: string[] = [];
  private _teacherList: any[] = [];

  private valid: boolean = false;
  private dialogVisibility: any = {};
  private rules = {
    name: [
      (v: string) =>
        /([A-Z]|[a-z ])+$/.test(v) || "Names must only include alphabets",
      (v: string) => !!v || "Name is required"
    ],
    radio: [(v: any) => v === false || !!v || "Required"],
    checkbox: [(v: boolean) => !!v || "You must agree to continue!"]
  };
  // populates based on form model and input data
  // ex. { name: Kavin, grade: 3, consent: True}
  data: any = this.consentFormData || {};

  mounted() {
    // only if the form model has the need for this data
    this.schoolAndTeacherDropdown();
  }

  schoolAndTeacherDropdown() {
    this.teacherNameEnable = false;
    this._schoolAndTeachers = {};
    this._schoolList = [];
    this._teacherList = [];

    configurationCollection
      .doc("schools")
      .get()
      .then(async (snapshot: any) => {
        let data = await snapshot.data();

        data["schools"].forEach(
          (schoolData: { name: string; active: boolean }) => {
            if (schoolData.active) {
              this._schoolList.push(schoolData.name);
            }
            this._schoolAndTeachers[schoolData.name] = [];
          }
        );
      })
      .finally(() => {
        this._schoolList.push("Not Affiliated with a school");
        usersCollection
          .where("accountType", "==", "teacher")
          .get()
          .then(async (snapshot: any) => {
            await snapshot.forEach((doc: any) => {
              let data: any = doc.data();
              if (this._schoolAndTeachers[data["schoolname"]]) {
                this._schoolAndTeachers[data["schoolname"]].push(doc);
              }
            });
          })
          .finally(() => {
            this.$forceUpdate();
          });
      });
  }

  private successNotification() {
    if (!this.showNotification) return;
    this.$store.dispatch("addNotification", {
      type: "success",
      value: `${this.title} submitted successfully. Thank you for participating.`,
      timeout: 6000,
      icon: "mdi-file-document"
    });
  }

  submit() {
    if (this.data.consent && this.callbackfn != null) {
      this.callbackfn();
      return;
    }

    if (!this.data.consent && this.callbackOnConsentReject) {
      this.callbackOnConsentReject();
      return;
    }

    if (
      !this.data.consent &&
      (this.type == "teacher" || this.type == "parent")
    ) {
      // this block will execute if someone disagrees
      // Form won't be submitted since, teacher consent form by itself doesn't contain any information.
      this.successNotification();
      this.$router.push("/");
      return;
    }

    // validate function exists
    if ((this.$refs.form as any).validate() && this.collection) {
      // send data to server
      this.collection
        .add({ ...this.data, ...{ dateSubmitted: Date.now() } })
        .then(docRef => {
          this.successNotification();
          if (this.data["consent"] != false) {
            this.$router.push({ path: this.routing });
          } else {
            this.$router.push({ path: "/" });
          }
        })
        .catch(err => {
          this.$store.dispatch("addNotification", {
            type: "error",
            value: `${this.title} failed to submit. Please try again.`,
            timeout: 6000,
            icon: "mdi-file-document"
          });
        });
    }
  }

  reset() {
    (this.$refs.form as any).resetValidation();
    (this.$refs.form as any).reset();
  }

  render(h: CreateElement): VNode {
    if (this._schoolList == undefined) {
      return <div></div>;
    }
    return (
      <Card title={this.title}>
        <v-img
          slot="preTitle"
          src={require("@/assets/oise-logo.png")}
          max-width="500"
        ></v-img>
        <v-container slot="mainContent" class="justify-center">
          <p>{this.$slots.description}</p>
          <v-row justify="space-around">
            {this.content.map((object: string[], i: number) => (
              <v-col>
                <v-btn
                  color="light"
                  dark
                  onClick={() => {
                    this.$set(this.dialogVisibility, this.content[i][0], true);
                  }}
                >
                  {object[0]}
                </v-btn>
                <v-dialog
                  v-model={this.dialogVisibility[this.content[i][0]]}
                  max-width="800px"
                >
                  <v-card cols="8">
                    <v-card-title>
                      <span>{object[0]}</span>
                    </v-card-title>
                    <v-card-text>
                      {this.$createElement("span", {
                        domProps: {
                          innerHTML: object[1]
                        }
                      })}
                    </v-card-text>
                  </v-card>
                </v-dialog>
              </v-col>
            ))}
          </v-row>
          <br />
          <p>{this.$slots.postDescription}</p>
          <v-form ref="form" v-model={this.valid}>
            {this._schoolList.includes("Not Affiliated with a school")
              ? this.renderForm(this._schoolList, this._schoolAndTeachers)
              : ""}
          </v-form>
          <v-card-actions class="justify-center">
            <v-container style={{ padding: 0 }}>
              <v-row justify="center">
                {this.showRecaptcha && (
                  <RecapchaContainer
                    site_key={process.env.VUE_APP_RECAPTCHA_SITE_KEY}
                    secret={process.env.VUE_APP_RECAPTCHA_SECRET_KEY}
                    verify={this.recaptcha_handler}
                    error={this.recaptcha_handler}
                    expire={this.recaptcha_handler}
                  />
                )}
              </v-row>
              <v-row justify="center" style={{ marginTop: "15px" }}>
                <v-btn
                  color="success"
                  onClick={this.submit}
                  disabled={!this.valid}
                >
                  {this.buttonLabel}
                </v-btn>
              </v-row>
            </v-container>
          </v-card-actions>
        </v-container>
      </Card>
    );
  }

  private recaptcha_handler(res?: any) {
    if (!this.showRecaptcha) return;
    this.data["recaptcha"] = res ? true : false;
    this.$forceUpdate();
  }

  renderForm(_schoolList: any, _schoolAndTeachers: any) {
    return (
      this.formModel &&
      this.formModel.map((m: FormModel) => {
        let key: string = m.key;
        let type = m.type || "text";
        let arr = [];

        if (type == "text") {
          arr.push(
            <v-text-field
              v-model={this.data[key]}
              counter="30"
              rules={
                key == "birthday" ? GlobalRules.dateRules : this.rules.name
              }
              label={m.label}
              required
            ></v-text-field>
          );
        } else if (type == "radio") {
          arr.push(
            <v-radio-group
              v-model={this.data[key]}
              rules={this.rules.radio}
              row
            >
              <v-label>{m.label}</v-label>
              {m.props &&
                m.props.map(
                  (object: { key: any; label: string; value: any }) => (
                    <v-radio
                      key={object.key}
                      label={object.label}
                      value={object.value}
                    ></v-radio>
                  )
                )}
            </v-radio-group>
          );
        } else if (type == "checkbox") {
          arr.push(
            <v-checkbox
              v-model={this.data[key]}
              counter="30"
              rules={this.rules.checkbox}
              label={m.label}
              required
              disabled={m.disabled}
              style={{ display: m.hidden ? "none" : "initial" }}
            ></v-checkbox>
          );
        } else if (type == "checkboxGroup") {
          if (!(m.key in this.data)) this.$set(this.data, m.key, new Array());
          arr.push(
            <span>
              <v-item-group>
                <v-label>{m.label}</v-label>
                <v-row
                  style={{
                    marginLeft: "20px",
                    margin: "10px 0",
                    width: "90%"
                  }}
                >
                  {m.props &&
                    m.props.map(
                      (
                        object: {
                          key: any;
                          label: string;
                          value: any;
                          other: string | null;
                        },
                        i
                      ) => (
                        <v-checkbox
                          v-model={this.data[key]}
                          key={object.key}
                          label={object.label}
                          value={object.value}
                          style={{
                            margin: "10px 5px"
                          }}
                          rules={[
                            () =>
                              this.data[key].length > 0 ||
                              "Please select at least one of the above."
                          ]}
                          hide-details={i != m.props.length - 1}
                        ></v-checkbox>
                      )
                    )}
                </v-row>
              </v-item-group>
              <v-item-group>
                {this.data[key].indexOf("other") >= 0 ? ( // note that this is hard coded.
                  <span>
                    <p>
                      If you've checked <em>Other</em>, please list them here.
                    </p>
                    <v-text-field
                      v-model={this.data["otherGrade"]}
                      key="other"
                      label="Other"
                      required
                    ></v-text-field>
                  </span>
                ) : (
                  ""
                )}
              </v-item-group>
            </span>
          );
        } else if (type == "dropdown" && key == "schoolAndTeacher") {
          arr.push(
            <v-autocomplete
              name="schoolname"
              hide-no-data
              v-model={this.data["schoolname"]}
              items={_schoolList}
              item-text={_schoolList}
              prepend-icon="mdi-magnify"
              onchange={() => {
                this.data["teachername"] = null;
                this.teacherNameEnable =
                  this.data["schoolname"] !=
                  _schoolList[_schoolList.length - 1];
                this._teacherList = [];
                if (!this.teacherNameEnable || !this._teacherList) return;
                for (let doc of _schoolAndTeachers[this.data["schoolname"]]) {
                  let data = (doc as any).data();
                  this._teacherList.push({
                    name: data["firstname"] + " " + data["lastname"],
                    id: doc.id
                  });
                }
                this.$forceUpdate();
              }}
              placeholder="Start typing to search"
              label={
                this.type == "parent" ? "School Name" : "Your school's name"
              }
              required
              rules={[(v: any) => !!v || "Required"]}
            ></v-autocomplete>
          );
          arr.push(
            <v-autocomplete
              name="teachername"
              disabled={
                !this.teacherNameEnable || this._teacherList.length <= 0
              }
              prepend-icon="mdi-magnify"
              hide-no-data
              items={this._teacherList}
              item-text={"name"}
              v-model={this.data["teachername"]}
              autocomplete
              label={
                this.type == "parent" ? "Teacher Name" : "Your teacher's name"
              }
              return-object
              required
              rules={
                this.teacherNameEnable && this._teacherList.length > 0
                  ? [(v: any) => !!v || "Required"]
                  : []
              }
            ></v-autocomplete>
          );
        } else {
          // type == 'label'
          arr.push(
            <div style="margin: 20px 0;">
              {m.props && (m.props as any).other == "indent" ? (
                <span>&emsp;•&emsp;</span>
              ) : (
                ""
              )}
              <label style="color: black;">{m.label}</label>
            </div>
          );
        }
        return arr;
      })
    );
  }
}

const component = ConsentPage;
